import { useEffect, useRef } from "react"
import { useLayerProbeMutation } from "@/v2-backoffice-client/layer/probe/layer.probe.mutation"

/**
 * Fires one live probe (uptime verdict + metadata fingerprint for drift)
 * when a layer panel opens, so the operator sees current health instead
 * of waiting for the next cron pass. Layers the cron has never discovered
 * start being monitored on the spot.
 *
 * The uptime summary updates through the normalised UptimeTarget entity
 * the mutation returns; getUptimeTarget / drift are refetched explicitly
 * because their previous result may have been null (nothing monitored
 * yet), which entity normalisation alone cannot backfill.
 */
export function useLayerProbeOnOpen(featureCode: string | null | undefined) {
  const [runProbe, { loading }] = useLayerProbeMutation({
    refetchQueries: ["UptimeInlineByKeyQuery", "LayerDriftQuery"],
  })

  // One probe per layer per panel-open — not a polling loop. Reopening
  // the panel (component remount) re-arms it.
  const probedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!featureCode || probedRef.current === featureCode) return
    probedRef.current = featureCode
    runProbe({ variables: { input: { FeatureCode: featureCode } } }).catch(
      () => {
        // Errors surface through the Apollo error link; a failed probe
        // must not break the panel.
      },
    )
  }, [featureCode, runProbe])

  return { isProbing: loading }
}
